import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import { format } from 'date-fns';
import jwtDecode from 'jwt-decode';
import { Flex, Typography, Box, Button, Card, IconButton } from '@kistech/kis-ui';
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box as MuiBox,
  Grid,
  Typography as MuiTypography
} from '@mui/material';
import { ActionOverlay } from 'components/ActionOverlay';
import { CreateNewUserOverlay } from 'components/CreateNewUserOverlay';
import { UpdateUserDrawer } from 'components/UpdateUserDrawer';
import { useActions } from 'hooks/useActions';
import { selectors as adminsSelectors, actions as adminsActions, AdminWithActivity } from 'store/admins';
import { selectors as authSelectors } from 'store/auth';
import { selectors as clientsSelectors, Client } from 'store/clients';
import { formatTitle } from 'utils/stringFormat';
import { useStyles } from './styled';

// RETURNS USER FRIENDLY STRING FOR ROLE
const getAdminRole = (role: string) => {
  if (role.toLowerCase().includes('maintenance')) return 'maintenance';
  if (role.toLowerCase().includes('client')) return 'admin';
  if (role.toLowerCase().includes('readonly')) return 'Read-Only Admin';
  if (role.toLowerCase().includes('global')) return 'global admin';
  return '';
};

// RETURNS LATEST ACTIVITY TIME OR USER FRIENDLY STRING IF NULL
const getAdminLatestLog = (admin: AdminWithActivity) => {
  if (admin.latestActivity) return format(new Date(admin.latestActivity.createdAt), 'MMMM dd, yyyy');
  return '--';
};

// RETURNS LATEST ACTIVITY EVENT OR USER FRIENDLY STRING IF NULL
const getAdminLatestActivity = (admin: AdminWithActivity) => {
  if (admin.latestActivity) return admin.latestActivity.event;
  return '--';
};

// FORMATS THE CREATED DATE IN THE ADMIN OBJECT
const formatAdmins = (admins: AdminWithActivity[]) => {
  if (admins.length) {
    return admins.map((admin) => ({
      ...admin,
      createdAt: admin.createdAt ? format(new Date(admin.createdAt), 'MMMM dd, yyyy') : ''
    }));
  }
  return admins;
};

const UsersView: React.FC = () => {
  // ACTIONS AND SELECTORS
  const { getAdmins, deleteAdmin, resetError } = useActions({
    getAdmins: adminsActions.getAdmins,
    deleteAdmin: adminsActions.deleteAdmin,
    resetError: adminsActions.resetError
  });
  const client = useSelector(clientsSelectors.selectClient) as Client;
  const admins = useSelector(adminsSelectors.selectAdmins);
  const adminData = useSelector(authSelectors.selectAdmin);
  const { adminId } = jwtDecode<{ [x: string]: string | number }>(useSelector(authSelectors.selectToken) as string);
  const { pending: adminsPending, successful, error } = useSelector(adminsSelectors.selectAction);
  const [readOnlyAdmin, setReadOnlyAdmin] = useState(true);

  // STATE OF TOGGLE CREATE USER OVERLAY | UPDATE USER DRAWER
  const [showCreateNewUser, setShowCreateNewUser] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [deleteParams, setDeleteParams] = useState<null | AdminWithActivity>(null);

  const classes = useStyles({ showDrawer })();

  // STATE OF SELECT ADMIN
  const [selectedAdmin, setSelectedAdmin] = useState<AdminWithActivity | null>(null);

  // FORMATS THE ADMINS TO FIT HOW THE TABLE SHOULD DISPLAY THE DATA
  const formattedAdmins = useMemo(() => formatAdmins(admins), [admins]);

  // Setting loadMore to show scrollbar on table
  const loadMore = () => {};

  // ON UPDATE OF CLIENT || SUCCESSFUL
  React.useEffect(() => {
    // IF CLIENT EXIST OR SUCCESSFUL ACTION - GET ADMINS
    if (client || successful) getAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, successful]);

  // check for user is Read-Only Admin or not, if yes hide Edit column and Add user button
  React.useEffect(() => {
    setReadOnlyAdmin(Boolean(adminData && adminData.role === 'ReadOnlyAdmin'));
  }, [adminData]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // EDIT ICON CLICK
  const onEditClick = (admin: AdminWithActivity) => {
    // SETS THE SELECTED ADMIN
    setSelectedAdmin(admin);

    // SETS THE DRAWER TO SHOW
    setShowDrawer(true);
  };

  // ON DRAWER CLOSE
  const onDrawerClose = () => {
    // SETS THE DRAWER TO HIDE
    setShowDrawer(false);

    // RESET ERROR
    if (error) resetError();

    // SETS THE SELECTED ADMIN TO BE NULL
    setSelectedAdmin(null);
  };

  // IF NO CLIENT GO TO CLIENTS PAGE
  if (!client) return <Redirect to="/clients" />;

  return (
    <>
      {deleteParams && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={400}>
              <Typography size={40} weight={600} align="center" style={{ lineHeight: 1 }}>
                Are You Sure?
              </Typography>
            </Box>
          }
          description={
            <Box width={500}>
              <Typography size={24} align="center">
                Are you sure you want to delete this user?
              </Typography>
            </Box>
          }
          confirmText="Yes"
          appearance="error"
          onConfirm={() => {
            deleteAdmin({ id: deleteParams.id });
            setDeleteParams(null);
          }}
          cancelText="Cancel"
          onCancel={() => setDeleteParams(null)}
          height={300}
        />
      )}

      {/* HEADER ROW */}
      <Grid className={classes.usersHeading}>
        <Box flexOverride="none">
          <MuiTypography className={classes.usersText}>Users</MuiTypography>
        </Box>
        {!readOnlyAdmin && (
          <Button
            size="small"
            width={175}
            isLoading={adminsPending}
            onClick={() => setShowCreateNewUser(true)}
            className={classes.userAddNewButton}
          >
            Add User
          </Button>
        )}
      </Grid>

      {/* TABLE OF USERS */}
      <MuiBox className={classes.usersTableOuterBox}>
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <TableContainer onScroll={() => loadMore()} id="userViewTable" className={classes.usersTableContainer}>
              <MuiTable stickyHeader style={{ borderCollapse: 'collapse' }}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.usersHeadersUsertype}>User Type</TableCell>
                    <TableCell className={classes.usersTableHeaders}>Name</TableCell>
                    <TableCell className={classes.usersTableHeaders}>Last Logged</TableCell>
                    <TableCell className={classes.usersTableHeaders}>Last Activity</TableCell>
                    {!readOnlyAdmin && (
                      <TableCell className={classes.usersTableHeaders} style={{ textAlign: 'center', width: '125px' }}>
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formattedAdmins.map((usersObj: AdminWithActivity) => {
                    return (
                      <TableRow
                        className={classes.usersTableRows}
                        selected={
                          selectedAdmin && Object.keys(selectedAdmin).length
                            ? (selectedAdmin as AdminWithActivity).id === usersObj.id
                            : false
                        }
                        classes={{ selected: classes.selected }}
                      >
                        <TableCell className={classes.userRoleRow}>
                          <Typography size={16} weight={600}>
                            {formatTitle(getAdminRole(usersObj.role))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography size={16}>{usersObj.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography size={16}>{getAdminLatestLog(usersObj)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography size={16}>{formatTitle(getAdminLatestActivity(usersObj))}</Typography>
                        </TableCell>
                        {!readOnlyAdmin && (
                          <TableCell>
                            <Flex align="center" justify="end">
                              {adminId !== usersObj.id && (
                                <IconButton
                                  name="exit_circle"
                                  onClick={() => setDeleteParams(usersObj)}
                                  data-testid="delete-user-button"
                                />
                              )}
                              <IconButton
                                style={{ marginLeft: '10px' }}
                                name="edit_circle"
                                onClick={() => onEditClick(usersObj)}
                                data-testid="update-user-button"
                              />
                            </Flex>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </MuiTable>
            </TableContainer>
          </Box>
        </Card>
      </MuiBox>
      <Grid className={classes.footer}>
        <p className={classes.total}>Users: {formattedAdmins.length}</p>
      </Grid>

      {/* UPDATEUSERDRAWER COMPONENT */}
      {selectedAdmin && (
        <UpdateUserDrawer onClose={onDrawerClose} isOpen={showDrawer} admin={selectedAdmin as AdminWithActivity} />
      )}

      {/* CREATENEWUSEROVERLAY COMPONENT */}
      {showCreateNewUser && (
        <CreateNewUserOverlay
          onConfirm={() => setShowCreateNewUser(false)}
          onCancel={() => setShowCreateNewUser(false)}
        />
      )}
    </>
  );
};

export default UsersView;
